import * as React from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Button from '@mui/material/Button';
import { useAccount } from '../../auth';
import { BrowserTabTitle } from '../BrowserTabTitle';
import { PageTitle } from '../PageTitle';
import { useEntities } from '../../data/flowEntities';
import { getEntityType, getEntityCreateVerb } from '../../utils/entities';
import { capitalize } from '../../utils/strings';
import { gridContainerMinWidth, theme } from '../../utils/theme';
import AQDataGrid, { AQDataGridQueryParams } from '../AQDataGrid';
import getEntityListColumns from './EntityListColumnsDef';
import { Entity } from '../../types/entity.types';

export const EntityList = () => {
  const { currentAccount } = useAccount();
  const navigate = useNavigate();
  const { pluralEntityType } = useParams();
  const entityType = getEntityType(pluralEntityType);
  const createVerb = getEntityCreateVerb(entityType);

  // redirect to /connections if the entity type is not navigable
  React.useEffect(() => {
    const urlPaths = window.location.pathname.split('/');
    if (urlPaths.includes('integrations')) {
      navigate(`/${currentAccount.name}/connections`, { replace: true });
    }
  }, []);

  const buildQueryArgs = React.useCallback(
    (queryParams: AQDataGridQueryParams) => [
      currentAccount.id,
      entityType,
      {
        include: [`${entityType}-type`, 'health-status', 'aggregate'],
        ...queryParams,
      },
    ],
    [currentAccount.id, entityType],
  );

  const rowsTransform = React.useCallback(
    (entities: Entity[]) =>
      entities.map((entity) => ({
        ...entity,
        id: entity.id,
        health: entity?.['health-status']?.status || 'unknown',
        catalogType: entity[`${entityType}-type`] as {
          title: string;
          img_url?: string;
        },
        outboundFlowsCount: entity.aggregate?.outbound_flows_count ?? 0,
        inboundFlowsCount: entity.aggregate?.inbound_flows_count ?? 0,
      })),
    [entityType],
  );

  const gridProps = React.useMemo(
    () => ({
      getRowClassName: (params: { row: any }) =>
        `aq-datagrid-row--${params.row.health}`,
      getEstimatedRowHeight: () => 52,
      getRowHeight: () => 'auto' as const,
    }),
    [],
  );

  if (!entityType) {
    return null;
  }

  const colDefs = getEntityListColumns(entityType);

  const handleConnectClick = () => {
    navigate(`/${currentAccount.name}/${pluralEntityType}/${createVerb}`);
  };

  return (
    <div>
      <BrowserTabTitle>{capitalize(pluralEntityType)}</BrowserTabTitle>
      <PageTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          paddingRight: theme.spacing(8),
        }}
      >
        <Button
          variant="contained"
          color="success"
          onClick={handleConnectClick}
        >
          {capitalize(createVerb)}
        </Button>
      </PageTitle>
      <AQDataGrid
        dataQueryHook={useEntities}
        buildQueryArgs={buildQueryArgs}
        rowsTransformer={rowsTransform}
        colDefs={colDefs}
        gridProps={gridProps}
        sx={{
          width: gridContainerMinWidth,
          margin: '0 auto',
          '&.MuiDataGrid-root--densityStandard .MuiDataGrid-cell': {
            py: '15px',
          },
        }}
      />
    </div>
  );
};
